import { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react"
import history from "utils/history"
import { useParams } from "react-router-dom"

import { ILaundryProviderProps, ILaundryContext } from "./LaundryProviderTypes"
import { LAUNDRY_INITIAL_CONTEXT } from "./LaundryProviderConstants"

import useGetLaundry from "api/querries/laundry/useGetLaundry"
import useCreateUpdateLaundryMutation from "api/mutations/laundry/useCreateUpdateLaundry"
import { useUI } from "providers/UIProvider"
import { ILaundryHoliday } from "types/laundry"
import { IRoles } from "types/shared"
import { RESPONSE_MODAL_VARIANTS } from "components/shared/modals/ResponseModal/ResponseModalTypes"
import useGetLaundryChangesHistory from "api/querries/laundry/useGetLaundryChangesHistory"
import useApproveRejectLaundryUpdates from "api/mutations/laundry/useApproveRejectLaundryUpdates"
import useGetLaundryDistricts from "api/querries/laundry/useGetLaundryDistricts"
import { useAppContext } from "providers/AppProvider/AppProvider"

export const LaundryContext = createContext<ILaundryContext>(LAUNDRY_INITIAL_CONTEXT)

export const useLaundryContext: () => ILaundryContext = () => useContext(LaundryContext)

const LaundryProvider: React.FC<ILaundryProviderProps> = ({ children }) => {
  const urlParams = useParams()

  const { search, pathname } = history.location

  const { toggleModal, lang } = useUI()
  const { user, isAuthenticated, userRole } = useAppContext()
  const isAuthorized = user !== null
  const isQappUser = userRole && [IRoles.QAPP_ADMIN, IRoles.QAPP_MANAGER].includes(userRole)
  // const isEnabled = Boolean(urlParams?.laundryId) && pathname.includes("laundry")
  const addMode = Boolean(urlParams?.laundryId === "add-laundry")
  const [state, setState] = useReducer(
    (oldState: any, newState: any) => ({ ...oldState, ...newState }),
    LAUNDRY_INITIAL_CONTEXT,
  )
  const params = new URLSearchParams(search)
  const pageSize = Number(params.get("pageSize"))
  const searchVal = String(params.get("search"))

  const [currentPage, setCurrentPage] = useState(pageSize || 1)
  const [paramsState, setParamsState] = useState<any>(params)
  const [searchValue, setSearchValue] = useState<string>(searchVal === "null" ? "" : searchVal)

  const { data: laundryData, isFetched: isLaundryDataFetched, isLoading: laundryDataLoading, refetch: refetchLaundryData } = useGetLaundry({ carWashId: addMode ? 0 : Number(urlParams.laundryId) || undefined, enabled: (!user?.isQappAdmin && isAuthenticated) })
  const { data: laundryChangesHistoryData, isFetched: isLaundryChangesHistoryFetched } = useGetLaundryChangesHistory(Number(state.laundryId), state.enabledChangesHistory)
  const { mutate: createUpdateLaundry, isSuccess: createUpdateLaundrySuccess, isLoading: createUpdateLaundryLoading, data: updatedLaundryData } = useCreateUpdateLaundryMutation()
  const { data: laundryDistrictsData, isFetched: isLaundryDistrictsDataFetched, isLoading: laundryDistrictsDataLoading } = useGetLaundryDistricts(isAuthorized)

  const { mutate: approveRejectLaundryUpdates, isSuccess: approveRejectLaundryUpadtesSuccess, isLoading: approveRejectLaundryUpdatesLoading } = useApproveRejectLaundryUpdates()
  const updateSearchParams = (params: any) => {
    history.push({
      search: `?${new URLSearchParams(params).toString()}`
    })
  }
  const handleLaundriesPageChange = (page: number) => {
    setCurrentPage(page)
    updateSearchParams({ ...paramsState, pageSize: page })
    setParamsState({ ...paramsState, pageSize: page })
  }


  const handleOpenedClosedCheckboxClick = (isOpen: boolean) => {
    updateSearchParams({ ...paramsState, isOpen })
    setParamsState({ ...paramsState, isOpen })

  }

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
  }

  const handleSearchSubmit = () => {
    updateSearchParams({ ...paramsState, search: searchValue })
    setParamsState({ ...paramsState, search: searchValue })
  }

  const handleChangeCommission = (newCommission: any) => {
    setState({ commission: newCommission })
  }

  useEffect(() => {
    if (isLaundryChangesHistoryFetched) {
      setState({ changesHistory: laundryChangesHistoryData.data })
    }
  }, [laundryChangesHistoryData])

  useEffect(() => {
    if (isLaundryDataFetched) {
      setState({ laundry: laundryData.data })
    }
  }, [laundryData])

  useEffect(() => {
    if (isLaundryDataFetched && updatedLaundryData) {
      setState({ laundry: updatedLaundryData.data })
    }
  }, [updatedLaundryData])


  useEffect(() => {
    if (createUpdateLaundrySuccess) {
      toggleModal("responseModal", true, { variant: RESPONSE_MODAL_VARIANTS.SUCCESS })
      setTimeout(() => toggleModal("responseModal", false), 3000)
      isQappUser && history.push("/laundries")
    }
  }, [createUpdateLaundrySuccess])

  useEffect(() => {
    if (laundryDistrictsData && isLaundryDistrictsDataFetched) {
      setState({ districts: laundryDistrictsData.data })
    }
  }, [laundryDistrictsData])

  useEffect(() => {
    if (approveRejectLaundryUpadtesSuccess) {
      history.push("/laundries")
    }
  }, [approveRejectLaundryUpadtesSuccess])

  useEffect(() => {
    if (lang !== undefined && isLaundryDataFetched) {
      refetchLaundryData()
    }
  }, [lang])

  useEffect(() => {
    if (isLaundryDataFetched) {
      refetchLaundryData()
    }
  }, [location.reload])

  const handleLaundryHolidaysPageChange = () => {
    setState({ currentPage: state.currentPage + 1 })
  }

  const setCurrentHoliday = (holiday: ILaundryHoliday) => setState({ currentHoliday: holiday })

  const value = useMemo(() => ({
    ...state,
    setState,
    handleLaundryHolidaysPageChange,
    handleLaundriesPageChange,
    handleOpenedClosedCheckboxClick,
    setCurrentHoliday,
    createUpdateLaundry,
    createUpdateLaundrySuccess,
    createUpdateLaundryLoading,
    laundriesCurrentPage: currentPage,
    approveRejectLaundryUpdates,
    approveRejectLaundryUpdatesLoading,
    laundryDistrictsDataLoading,
    searchValue,
    handleSearchChange,
    handleSearchSubmit,
    handleChangeCommission,
    laundryDataLoading
  }), [state, createUpdateLaundrySuccess, createUpdateLaundryLoading, approveRejectLaundryUpdatesLoading, approveRejectLaundryUpadtesSuccess, laundryDistrictsDataLoading, searchValue, laundryDataLoading])

  return <LaundryContext.Provider value={value}>
    {children}
  </LaundryContext.Provider>
}

export default LaundryProvider